'use client'

import { useEffect, useReducer, useState, type ReactNode } from 'react'

import { renderDraftStoryAction } from '@/components/server/storyblok/render-draft-story-action'
import type { Locale } from '@/i18n'
import { installEditorProductTargets, isStoryblokVisualEditor } from '@/lib/storyblok/editor-product-targets'
import { subscribeLivePreview } from '@/lib/storyblok/live-preview-bridge'
import { createLivePreviewCommit, type LiveRender } from '@/lib/storyblok/live-preview-commit'
import { createLivePreviewQueue } from '@/lib/storyblok/live-preview-queue'
import { recoverLivePreviewToken } from '@/lib/storyblok/live-preview-session'

interface Options {
  storyblokId: number
  fullSlug: string
  locale: Locale
  segment: string
  token: string | null
}

const isVisualEditor = () => isStoryblokVisualEditor(window.location.search, window.self !== window.top)

export const useStoryblokLivePreview = ({ storyblokId, fullSlug, locale, segment, token }: Options): ReactNode => {
  const [live, setLive] = useState<LiveRender | null>(null)
  const [, retryRender] = useReducer((revision: number) => revision + 1, 0)

  useEffect(() => {
    if (isVisualEditor()) {
      return installEditorProductTargets(document)
    }
  }, [])

  // This effect runs after the replacement tree commits, including streamed
  // children that suspend. Receiving the action result alone is too early.
  useEffect(() => {
    live?.committed()
  }, [live])

  useEffect(() => {
    if (!isVisualEditor()) {
      return
    }
    const session = recoverLivePreviewToken(fullSlug, token)
    if (!session) {
      console.warn('[sb-live] no live-edit session token — preview will only update when you save')
    }

    const liveCommit = createLivePreviewCommit({ setLive, retry: retryRender })
    const queue = createLivePreviewQueue({
      render: async (content) => {
        const started = performance.now()
        const result = await renderDraftStoryAction({ content, fullSlug, locale, segment, token: session! })
        console.info('[sb-live] render response received (%dms)', Math.round(performance.now() - started))
        return result
      },
      commit: liveCommit.commit,
      onError: (error) => console.error('[sb-live] live render failed', error),
    })
    const unsubscribe = subscribeLivePreview((event) => {
      if (event.action === 'input' && event.story?.id === storyblokId && event.story.content != null && session) {
        queue.push(event.story.content)
      } else if ((event.action === 'change' || event.action === 'published') && event.storyId === storyblokId) {
        window.location.reload()
      }
    })
    return () => {
      unsubscribe()
      queue.dispose()
      liveCommit.dispose()
    }
  }, [storyblokId, fullSlug, locale, segment, token])

  return live?.node ?? null
}
